/**
 * Every attribute name Pax records. What is true of a whole process goes on its resource; what is
 * true of one operation goes on its span. The telemetry docs' Attributes page says what each means.
 */
export const PAX = {
  // Games and agents
  gameId: "pax.game.id",
  gameKind: "pax.game.kind",
  gameHeadRevision: "pax.game.head_revision",
  gameAfterRevision: "pax.game.after_revision",
  agentId: "pax.agent.id",
  agentRole: "pax.agent.role",
  agentStopRequested: "pax.agent.stop_requested",

  // Activities: one agent doing one unit of work
  activityKind: "pax.activity.kind",
  activityLabel: "pax.activity.label",
  activityKey: "pax.activity.key",
  activityAnswering: "pax.activity.answering",
  activityReadThroughRevision: "pax.activity.read_through_revision",
  activityResultedIn: "pax.activity.resulted_in",
  contextNewlyVisible: "pax.context.newly_visible",
  activityOutcome: "pax.activity.outcome",
  attempt: "pax.attempt",

  // Proposals, deltas, and the game server
  idempotencyKey: "pax.idempotency_key",
  proposalDeltaCount: "pax.proposal.delta_count",
  proposalStatus: "pax.proposal.status",
  proposalThroughRevision: "pax.proposal.through_revision",
  proposalOperations: "pax.proposal.operations",
  proposalRejected: "pax.proposal.rejected",
  proposalIssues: "pax.proposal.issues",
  revision: "pax.revision",
  deadline: "pax.deadline",

  // Sockets and reconnecting
  socketFrameType: "pax.socket.frame_type",
  socketCloseCode: "pax.socket.close_code",
  socketCloseReason: "pax.socket.close_reason",
  socketCloseClean: "pax.socket.close_clean",
  reconnectReason: "pax.reconnect.reason",
  reconnectAttempts: "pax.reconnect.attempts",
  connectReason: "pax.connect.reason",

  // Browser actions and operations
  forkGameId: "pax.fork.game_id",
  action: "pax.action",
  operationName: "pax.operation.name",
  presetSlug: "pax.preset.slug",
  presetVersionId: "pax.preset.version_id",
  draftId: "pax.draft.id",

  // Conversations, tools, and agent backoff
  conversationKind: "pax.conversation.kind",
  conversationLabel: "pax.conversation.label",
  conversationGeneration: "pax.conversation.generation",
  conversationCompacted: "pax.conversation.compacted",
  conversationPromptTokens: "pax.conversation.prompt_tokens",
  conversationSummaryTokens: "pax.conversation.summary_tokens",
  toolDescription: "pax.tool.description",
  toolQuery: "pax.tool.query",
  backoffReason: "pax.backoff.reason",
  backoffMs: "pax.backoff.ms",
  graderAttempt: "pax.grader.attempt",

  // Model calls, beside the GenAI conventions
  modelCallId: "pax.model_call.id",
  modelTransport: "pax.model.transport",
  modelCostUsd: "pax.model.cost_usd",
  modelUpstreamProvider: "pax.model.upstream_provider",
  modelRegion: "pax.model.region",
  modelNativeFinishReasons: "pax.model.native_finish_reasons",
  modelUpstreamCostUsd: "pax.model.upstream_cost_usd",
  errorFault: "pax.error.fault",
  errorCode: "pax.error.code",
  outputErrorType: "pax.output_error.type",
  outputErrorMessage: "pax.output_error.message",

  // Stream events on a `chat` span
  streamFirstByte: "pax.stream.first_byte",
  streamFirstChunk: "pax.stream.first_chunk",
  streamReasoningStarted: "pax.stream.reasoning_started",
  streamReasoningDone: "pax.stream.reasoning_done",
  streamTextStarted: "pax.stream.text_started",
  streamToolCallStarted: "pax.stream.tool_call_started",
  streamToolArgumentsDone: "pax.stream.tool_arguments_done",
  streamFinish: "pax.stream.finish",
  streamUsage: "pax.stream.usage",

  // Evaluations
  evalRunId: "pax.eval.run.id",
  evalTrialId: "pax.eval.trial.id",
  evalRoundsPlayed: "pax.eval.rounds_played",
  evalScore: "pax.eval.score",

  // Risk, service callers, and the telemetry pipeline
  riskEvent: "pax.risk.event",
  serviceCaller: "pax.service_caller",
  traceId: "pax.trace_id",
  telemetryRebuiltRoot: "pax.telemetry.rebuilt_root",
  telemetryDroppedAttributes: "pax.telemetry.dropped_attributes",
} as const;

/**
 * Names from the OpenTelemetry GenAI semantic conventions. The conventions are still in
 * Development, so every name Pax emits is pinned here.
 * https://github.com/open-telemetry/semantic-conventions-genai/blob/main/docs/gen-ai/gen-ai-spans.md
 */
export const GEN_AI = {
  // What ran: the operation, provider, agent, conversation, and tool
  operationName: "gen_ai.operation.name",
  providerName: "gen_ai.provider.name",
  agentName: "gen_ai.agent.name",
  conversationId: "gen_ai.conversation.id",
  toolName: "gen_ai.tool.name",
  toolCallId: "gen_ai.tool.call.id",
  toolCallResult: "gen_ai.tool.call.result",

  // What was asked
  requestModel: "gen_ai.request.model",
  requestMaxTokens: "gen_ai.request.max_tokens",
  requestTemperature: "gen_ai.request.temperature",
  requestTopP: "gen_ai.request.top_p",
  requestStream: "gen_ai.request.stream",
  requestReasoningLevel: "gen_ai.request.reasoning.level",
  outputType: "gen_ai.output.type",

  // What came back
  responseId: "gen_ai.response.id",
  responseModel: "gen_ai.response.model",
  responseFinishReasons: "gen_ai.response.finish_reasons",
  responseTimeToFirstChunk: "gen_ai.response.time_to_first_chunk",
  openaiResponseServiceTier: "openai.response.service_tier",

  // Token usage
  usageInputTokens: "gen_ai.usage.input_tokens",
  usageOutputTokens: "gen_ai.usage.output_tokens",
  usageCacheReadInputTokens: "gen_ai.usage.cache_read.input_tokens",
  usageCacheWriteInputTokens: "gen_ai.usage.cache_write.input_tokens",
  usageReasoningOutputTokens: "gen_ai.usage.reasoning.output_tokens",
} as const;

/**
 * Names from the OpenTelemetry semantic conventions that Pax records, pinned like {@link GEN_AI}
 * since many are still in Development. The `geo.country.name`, `network.as.*`, `device.type`, and
 * `rum.sessionId` names aren't in the conventions; HyperDX's views read them.
 * https://opentelemetry.io/docs/specs/semconv/
 */
export const OTEL = {
  // Who wrote it: service, build, and environment
  serviceName: "service.name",
  serviceVersion: "service.version",
  serviceInstanceId: "service.instance.id",
  deploymentEnvironmentName: "deployment.environment.name",

  // Who it was for
  sessionId: "session.id",
  rumSessionId: "rum.sessionId",
  userId: "user.id",

  // HTTP
  httpRequestMethod: "http.request.method",
  httpRoute: "http.route",
  httpResponseStatusCode: "http.response.status_code",
  urlFull: "url.full",
  urlPath: "url.path",
  urlScheme: "url.scheme",
  serverAddress: "server.address",
  serverPort: "server.port",
  clientAddress: "client.address",
  userAgentOriginal: "user_agent.original",

  // Errors and processes
  errorType: "error.type",
  errorMessage: "error.message",
  exceptionType: "exception.type",
  exceptionMessage: "exception.message",
  exceptionStacktrace: "exception.stacktrace",
  processExitCode: "process.exit.code",

  // Crons, queues, and databases
  faasTrigger: "faas.trigger",
  faasCron: "faas.cron",
  messagingSystem: "messaging.system",
  messagingOperationType: "messaging.operation.type",
  messagingOperationName: "messaging.operation.name",
  messagingDestinationName: "messaging.destination.name",
  messagingMessageId: "messaging.message.id",
  messagingBatchMessageCount: "messaging.batch.message_count",
  dbQueryText: "db.query.text",
  dbResponseStatusCode: "db.response.status_code",

  // Where a client is, from Cloudflare: on server spans, and on browser resources from the relay
  geoContinentCode: "geo.continent.code",
  geoCountryIsoCode: "geo.country.iso_code",
  geoCountryName: "geo.country.name",
  geoRegionIsoCode: "geo.region.iso_code",
  geoLocalityName: "geo.locality.name",
  networkAsNumber: "network.as.number",
  networkAsOrganizationName: "network.as.organization.name",
  userAgentName: "user_agent.name",
  userAgentVersion: "user_agent.version",
  userAgentOsName: "user_agent.os.name",
  userAgentOsVersion: "user_agent.os.version",
  deviceType: "device.type",
  deviceManufacturer: "device.manufacturer",
  deviceModelName: "device.model.name",
} as const;

/** What telemetry-tail adds to each invocation's root from Cloudflare's own record of it. */
export const CLOUDFLARE = {
  outcome: "cloudflare.outcome",
  cpuTimeMs: "cloudflare.cpu_time_ms",
  wallTimeMs: "cloudflare.wall_time_ms",
  truncated: "cloudflare.truncated",
  scriptName: "cloudflare.script_name",
  entrypoint: "cloudflare.entrypoint",
  durableObjectId: "cloudflare.durable_object_id",
} as const;
